import { Clear } from "@mui/icons-material";
import { Box, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, TextField } from "@mui/material";
import { addMonths, format, subMonths } from "date-fns";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { ComissionServices, LivveInvoiceRow, LivveInvoiceStatus } from "../../../../shared/services/api/comission/ComissionServices";
import { ModalConfirmSelect } from "../Modals/ModalConfirmSelect";
import { TableLivve } from "../TableLivve";

type StatusFilterOption = LivveInvoiceStatus | "todos";

// Mesmo padrão de "Período" usado no resto do Financeiro (ver ComissionToolbar/GenericToolbar),
// adaptado pra ciclo: o backend filtra por um único mês de referência (yyyy-MM), não por intervalo.
type CicloQuickOption = "todos" | "mes_anterior" | "1_mes" | "proximo_mes" | "personalizado";

const getCycleFromQuickOption = (option: CicloQuickOption): string => {
	const now = new Date();
	switch (option) {
		case "mes_anterior":
			return format(subMonths(now, 1), "yyyy-MM");
		case "1_mes":
			return format(now, "yyyy-MM");
		case "proximo_mes":
			return format(addMonths(now, 1), "yyyy-MM");
		default:
			return "";
	}
};

export const TabLivve = () => {
	const { debounce } = useDebounce();
	const toastHandler = useToast();
	// GET /comission/livve não tem busca por nome (manual v2.1, seção 2.5) — só status, clientId e
	// reference (ciclo). Sem tela de imobiliária pronta aqui, filtramos por ciclo (yyyy-MM).
	const [cicloQuickOption, setCicloQuickOption] = useState<CicloQuickOption>("todos");
	const [reference, setReference] = useState("");
	const [status, setStatus] = useState<StatusFilterOption>("todos");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [rows, setRows] = useState<LivveInvoiceRow[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);

	const [sendTarget, setSendTarget] = useState<LivveInvoiceRow | null>(null);
	const [isSending, setIsSending] = useState(false);
	const [paymentTarget, setPaymentTarget] = useState<LivveInvoiceRow | null>(null);
	const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);

	// Evita mandar "todos" como valor de status pro backend (mesmo padrão de TabComission).
	const normalizeStatus = (raw: StatusFilterOption): LivveInvoiceStatus | "" => (raw === "todos" ? "" : raw);

	const handleFetchData = (currentPage: number, limit: number, currentStatus: StatusFilterOption, currentReference: string) => {
		setIsLoading(true);
		debounce(() => {
			ComissionServices.getLivveInvoices(normalizeStatus(currentStatus), currentPage + 1, limit, "", currentReference)
				.then((response) => {
					if (response instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title="Erro" content={response.message} />,
						});
					} else {
						setRows(response.data);
						setTotalCount(response.totalCount ?? 0);
					}
				})
				.finally(() => setIsLoading(false));
		});
	};

	useEffect(() => {
		setPage(0);
		handleFetchData(0, rowsPerPage, status, reference);
	}, [refreshTrigger, status, reference]);

	const handleChangePage = (newPage: number) => {
		setPage(newPage);
		handleFetchData(newPage, rowsPerPage, status, reference);
	};

	const handleChangeRowsPerPage = (newRowsPerPage: number) => {
		setRowsPerPage(newRowsPerPage);
		setPage(0);
		handleFetchData(0, newRowsPerPage, status, reference);
	};

	const handleRefresh = () => setRefreshTrigger((prev) => prev + 1);

	const handleCicloChange = (e: SelectChangeEvent) => {
		const value = e.target.value as CicloQuickOption;
		setCicloQuickOption(value);
		if (value !== "personalizado") {
			setReference(getCycleFromQuickOption(value));
		}
	};

	const handleConfirmSend = async () => {
		if (!sendTarget) return;
		setIsSending(true);
		try {
			const result = await ComissionServices.sendToLivve(sendTarget.id);
			if (result instanceof Error) {
				toastHandler.present({ type: "error", position: "top-right", messageOrContent: <ToastContent title="Erro" content={result.message} /> });
				return;
			}
			// generate-order (e o atalho /livve/send) é protegido contra duplicidade — se a fatura já
			// tinha pedido, ela volta em `skipped`, não em `comissions` (manual v2.1, seção 2.2).
			if (result.comissions.length > 0) {
				toastHandler.present({
					type: "success",
					position: "top-right",
					messageOrContent: <ToastContent title="Sucesso" content={`Fatura de ${sendTarget.clientName} enviada pra Livve.`} />,
				});
			} else {
				const reason = result.skipped[0]?.reason ?? "já possui pedido Livve gerado";
				toastHandler.present({
					type: "info",
					position: "top-right",
					messageOrContent: <ToastContent title="Nada a enviar" content={`Fatura de ${sendTarget.clientName}: ${reason}.`} />,
				});
			}
			setSendTarget(null);
			handleRefresh();
		} finally {
			setIsSending(false);
		}
	};

	const handleConfirmPayment = async () => {
		if (!paymentTarget) return;
		setIsConfirmingPayment(true);
		try {
			const result = await ComissionServices.confirmLivvePayment(paymentTarget.id);
			if (result instanceof Error) {
				toastHandler.present({ type: "error", position: "top-right", messageOrContent: <ToastContent title="Erro" content={result.message} /> });
				return;
			}
			// Endpoint é idempotente e pode ser chamado antes do boleto ser pago — nesse caso não é
			// erro, só ainda não tem o que creditar (ver manual de integração Livve, seção 2.3/10).
			if ("itemsProcessed" in result) {
				toastHandler.present({
					type: "success",
					position: "top-right",
					messageOrContent: (
						<ToastContent title="Sucesso" content={`${result.itemsProcessed} item(ns) de ${paymentTarget.clientName} processado(s) e pontos creditados.`} />
					),
				});
				setPaymentTarget(null);
				handleRefresh();
			} else {
				toastHandler.present({
					type: "info",
					position: "top-right",
					messageOrContent: <ToastContent title="Aguardando pagamento" content={`Boleto de ${paymentTarget.clientName} ainda não foi pago (${result.status}).`} />,
				});
			}
		} finally {
			setIsConfirmingPayment(false);
		}
	};

	return (
		<Box>
			<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={1} mx={1}>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} sm={4} md={3}>
						<FormControl fullWidth size="small">
							<InputLabel id="livve-status-label">Status</InputLabel>
							<Select
								labelId="livve-status-label"
								value={status}
								label="Status"
								onChange={(e: SelectChangeEvent) => setStatus(e.target.value as StatusFilterOption)}
							>
								<MenuItem value="todos">Todos</MenuItem>
								<MenuItem value="ready">Pronta pra enviar</MenuItem>
								<MenuItem value="sent">Enviada</MenuItem>
								<MenuItem value="paid">Paga</MenuItem>
							</Select>
						</FormControl>
					</Grid>

					<Grid item xs={12} sm={4} md={3}>
						<FormControl fullWidth size="small">
							<InputLabel id="livve-ciclo-label">Ciclo</InputLabel>
							<Select labelId="livve-ciclo-label" value={cicloQuickOption} label="Ciclo" onChange={handleCicloChange}>
								<MenuItem value="todos">Todos os ciclos</MenuItem>
								<MenuItem value="mes_anterior">Mês Anterior</MenuItem>
								<MenuItem value="1_mes">Mês Atual</MenuItem>
								<MenuItem value="proximo_mes">Próximo Mês</MenuItem>
								<MenuItem value="personalizado">Personalizado</MenuItem>
							</Select>
						</FormControl>
					</Grid>

					{cicloQuickOption === "personalizado" && (
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								size="small"
								type="month"
								label="Ciclo (mês/ano)"
								value={reference}
								onChange={(e) => setReference(e.target.value)}
								InputLabelProps={{ shrink: true }}
								InputProps={{
									endAdornment: reference && (
										<InputAdornment position="end">
											<IconButton size="small" onClick={() => setReference("")}>
												<Clear fontSize="small" />
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</Grid>
					)}
				</Grid>
			</Box>

			<LoadingComp isopen={isLoading} />

			<TableLivve
				rows={rows}
				totalCount={totalCount}
				page={page}
				rowsPerPage={rowsPerPage}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				onClickSend={setSendTarget}
				onClickConfirmPayment={setPaymentTarget}
			/>

			<ModalConfirmSelect
				open={Boolean(sendTarget)}
				onClose={() => setSendTarget(null)}
				onConfirm={handleConfirmSend}
				title="Enviar fatura pra Livve"
				description={`A fatura de ${sendTarget?.clientName ?? ""} (${sendTarget?.reference ?? ""}) será enviada pra campanha da Livve.`}
				confirmLabel="Sim, enviar"
				cancelLabel="Cancelar"
				isLoading={isSending}
			/>

			<ModalConfirmSelect
				open={Boolean(paymentTarget)}
				onClose={() => setPaymentTarget(null)}
				onConfirm={handleConfirmPayment}
				title="Confirmar pagamento"
				description={`Verifica na Livve se o boleto de ${paymentTarget?.clientName ?? ""} já foi pago. Se sim, credita os pontos e marca a comissão como paga — pode chamar de novo sem problema se ainda não tiver sido pago (idempotente).`}
				confirmLabel="Sim, verificar"
				cancelLabel="Cancelar"
				isLoading={isConfirmingPayment}
			/>
		</Box>
	);
};
